import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, InputSignal, signal, ViewChild, WritableSignal } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import {
  IconButtonComponent,
  TableCellDirective,
  TableColumnDirective,
  TableComponent,
  TableHeaderDirective,
  TableRowCollapsibleDirective,
} from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import type { OrderHistoryRecordModel } from '@libs/services/models';
import { LinkDirective, LuxonDatePipe, PriceVisibilityDirective } from '@libs/shared/ui';
import { SrmCreateFormComponent } from '../../srm/srm-create-form';
import { OrderHistoryActionsComponent } from '../order-history-actions';
import { OrderHistoryDocumentsComponent } from '../order-history-documents';
import { OrderHistoryHelperService } from '../order-history-helper.service';
import type { OrderHistoryExpandedPanelModel, OrderHistoryPanelType } from '../order-history-panel.model';
import { OrderHistoryStatusComponent } from '../order-history-status';

@Component({
  selector: 'ui-order-history-desktop',
  templateUrl: './order-history-desktop.component.html',
  styleUrls: ['./order-history-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    IconComponent,
    IconButtonComponent,
    LinkDirective,
    MatTooltip,
    OrderHistoryActionsComponent,
    OrderHistoryDocumentsComponent,
    OrderHistoryStatusComponent,
    PriceVisibilityDirective,
    SrmCreateFormComponent,
    TableComponent,
    TableCellDirective,
    TableColumnDirective,
    TableHeaderDirective,
    TableRowCollapsibleDirective,
    LuxonDatePipe,
  ],
})
export class OrderHistoryDesktopComponent {
  @ViewChild(TableComponent) public table?: TableComponent;

  public readonly helperService: OrderHistoryHelperService = inject(OrderHistoryHelperService);

  public orders: InputSignal<OrderHistoryRecordModel[]> = input.required();

  public panelExpanded: WritableSignal<OrderHistoryExpandedPanelModel | undefined> = signal(undefined);

  public invoiceNumber(order: OrderHistoryRecordModel): string {
    return this.helperService.invoiceNumber(order);
  }

  public orderNumber(order: OrderHistoryRecordModel): string {
    return this.helperService.orderNumber(order);
  }

  public orderLink(order: OrderHistoryRecordModel): string {
    return this.helperService.orderLink(order);
  }

  // -- Collapsible row panels (SRM create form, receipt documents)

  public onOpenSrmForm(orderId: string): void {
    this.togglePanel(orderId, 'srm');
  }

  public onOpenDocuments(orderId: string): void {
    this.togglePanel(orderId, 'documents');
  }

  public onPanelClosed(): void {
    this.collapseExpandedRow();
    this.panelExpanded.set(undefined);
  }

  private togglePanel(orderId: string, panel: OrderHistoryPanelType): void {
    const order: OrderHistoryRecordModel | undefined = this.orders().find(orderHistoryRecord => orderHistoryRecord.id === orderId);

    if (!order || !this.table) {
      return;
    }

    const isSamePanelOpen: boolean = this.panelExpanded()?.orderId === orderId && this.panelExpanded()?.panel === panel;

    this.collapseExpandedRow();

    // If the same panel was already open for this order, only close it
    if (isSamePanelOpen) {
      this.panelExpanded.set(undefined);

      return;
    }

    this.table.expandedRows.add(order);
    order.rowClasses = panel === 'documents' ? 'documents-panel-open' : '';
    this.panelExpanded.set({ orderId, panel });
  }

  // Keeps the table row expansion in sync with the expanded panel
  private collapseExpandedRow(): void {
    const orderExpanded: OrderHistoryRecordModel | undefined = this.orders().find(
      orderHistoryRecord => orderHistoryRecord.id === this.panelExpanded()?.orderId,
    );

    if (orderExpanded) {
      this.table?.expandedRows.delete(orderExpanded);
      orderExpanded.rowClasses = '';
    }
  }
}
